import type { ReactNode } from "react";
import { PickerChainContext, PickerLevelProvider } from "./PickerChainContext";
import {
    PickerQueryContext,
    PickerRootContext,
    type PickerRootContextValue,
} from "./PickerContext";
import {
    PickerListContent,
    type PickerListContentProps,
} from "./PickerListContent";
import type { PickerListRootState } from "./usePickerListRoot";
import type { PickerClassNames, PickerItemValue } from "./Picker.types";

/** Default for an omitted `classNames`; module-scope so the root context memo stays stable. */
export const EMPTY_PICKER_CLASS_NAMES: PickerClassNames = {};

/**
 * The providers every list root (and every flyout level) wraps its Base UI
 * element in: root state, query state, the chain and the level's flyout
 * pair. `contextValue` lets a level override the root half (its own anchor
 * and search autofocus).
 */
export const PickerRootShell = <T extends PickerItemValue>({
    root,
    contextValue = root.contextValue,
    closeLevel,
    children,
}: {
    root: PickerListRootState<T>;
    contextValue?: PickerRootContextValue;
    closeLevel?: () => void;
    children: ReactNode;
}) => (
    <PickerRootContext.Provider value={contextValue}>
        <PickerQueryContext.Provider value={root.queryValue}>
            <PickerChainContext.Provider value={root.chainValue}>
                <PickerLevelProvider
                    depth={root.depth}
                    openFlyoutId={root.openFlyoutId}
                    setOpenFlyoutId={root.setOpenFlyoutId}
                    closeLevel={closeLevel}
                >
                    {children}
                </PickerLevelProvider>
            </PickerChainContext.Provider>
        </PickerQueryContext.Provider>
    </PickerRootContext.Provider>
);

type RootDrivenContentProps =
    | "groups"
    | "query"
    | "nested"
    | "onExpandedChange"
    | "footer";

/** `PickerListContent` fed from a list root: rows, filter text, nesting, expansion and footer. */
export const PickerRootListContent = <T extends PickerItemValue>({
    root,
    ...props
}: Omit<PickerListContentProps<T>, RootDrivenContentProps> & {
    root: PickerListRootState<T>;
}) => (
    <PickerListContent
        {...props}
        groups={root.groups}
        query={root.filterQuery}
        nested={root.nested}
        onExpandedChange={root.setExpanded}
        footer={root.footer}
    />
);
